import React, { useEffect, useRef, useState } from "react";
import { Episode } from "../../types/episode";
import EpisodeList from "./EpisodeList";
import useVideoCleanup from "../../hooks/useVideoCleanup";

interface VideoPlayerProps {
  videoUrl: string;
  episodes: Episode[];
  onPlayEpisode: (url: string) => void;
  onUnmount?: () => void; // Add callback for when player unmounts
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videoUrl,
  episodes,
  onPlayEpisode,
  onUnmount,
}) => {
  const playerRef = useRef<any>(null);
  const hlsInstanceRef = useRef<any>(null);
 
  useEffect(() => {
    let mounted = true;

    const initializePlayer = async () => {
      try {
        const [{ default: Artplayer }, { default: Hls }] = await Promise.all([
          import('artplayer'),
          import('hls.js')
        ]);

        if (!mounted) return;

        if (!playerRef.current) {
          playerRef.current = new Artplayer({
            container: ".artplayer-container",
            url: videoUrl,
            autoplay: true,
            fullscreen: true,
            customType: {
              m3u8: (videoElement: HTMLVideoElement, url: string) => {
                if (Hls.isSupported()) {
                  if (hlsInstanceRef.current) {
                    hlsInstanceRef.current.destroy();
                  }
                  const hls = new Hls();
                  hlsInstanceRef.current = hls;
                  hls.loadSource(url);
                  hls.attachMedia(videoElement);
                } else if (videoElement.canPlayType("application/vnd.apple.mpegurl")) {
                  videoElement.src = url;
                }
              },
            },
          });
         } else {
          playerRef.current.url = videoUrl;
        }
      } catch (error) {
        console.error("Error initializing video player:", error);
      }
    };

    initializePlayer();

    return () => {
      mounted = false;
      // Cleanup Artplayer and HLS instance
      if (playerRef.current) {
        try {
          playerRef.current.pause();
          playerRef.current.video.src = "";
          playerRef.current.video.load();
        } catch (error) {
          console.error("Error stopping video:", error);
        }
        playerRef.current.destroy();
        playerRef.current = null;
      }

      if (hlsInstanceRef.current) {
        hlsInstanceRef.current.destroy();
        hlsInstanceRef.current = null;
      }

      if (onUnmount) {
        onUnmount();
      }
    };
  }, [videoUrl, onUnmount]);

  return (
    <div className="flex flex-col md:flex-row gap-4 mb-4">
      <div
        className={`artplayer-container ${episodes.length === 1 ? 'w-full  h-[40vh] md:h-[60vh] lg:h-[70vh] xl:h-[80vh]' : 'w-full md:[70vw] h-[40vh] md:h-[60vh] lg:h-[70vh] xl:h-[80vh]'}`}
      ></div>
      {episodes.length > 1 && (
        <div className="episode-list-container w-full md:[29vw] lg:w-[20vw] md:w-full">
          <EpisodeList
            currentVideoUrl={videoUrl}
            episodes={episodes}
            movieSlug=""
            onPlayEpisode={(url) => {
              onPlayEpisode(url);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
